/**
 * A NAV ÉS TENSOFT SZELETE — a #25 negyedik szelete a felületen.
 *
 * ═══ MIÉRT A LAPON, ÉS NEM ABLAKBAN ═══
 *
 * Viktor döntése artboardon (2026-09-13, `docs/tervezes/design/nav-tensoft/`):
 * az „A" forma, a BEÁGYAZOTT PANEL. A kereső balra, a négy mező jobbra,
 * egyszerre látszanak. Két érv döntött. Egy: a másik három szelet is a LAPON
 * dolgozik — egy ablak itt a lapon a réteget a rétegre tenné, és az `Escape`
 * kétértelművé válna („melyiket zárja?"). Kettő: a következő cég
 * kiválasztásához nem kell semmit becsukni, ami ~80 cég bekötésénél nem
 * apróság.
 *
 * ⚠️ AMIT EZÉRT FIZETÜNK: a bevitel MOST KÉT KERETBEN él — itt és a paletta
 * ablakában (`NavCredentialsDialog`). Ezért közösek a darabok
 * (`NavCredentialParts`) és a SZABÁLYOK (`useNavCredentialEditor`): a mezők
 * ürülése cégváltásnál, a titkok törlése mentés után, a nyugtázás célja. Ha
 * egy ilyen szabály csak az egyik keretben állna, a másik TITKOT
 * szivárogtatna, némán.
 *
 * ⚠️ A SZABÁLYOK KÖZÖSEK, A PÉLDÁNYOK NEM. Az `App.tsx` a lapot és az ablakot
 * EGYSZERRE is felrakhatja (a paletta a lapon is él), és akkor két
 * `useNavCredentials` fut, két `GET /nav/credentials`-szal. A fölös kérés
 * nem baj; a következménye igen: ha az ablakban mentesz, a LAP keresője még
 * „nincs bekötve"-t mutat, amíg el nem hagyod a fület — mert a `reload()`-ot
 * csak az ablak példánya hívta meg.
 *
 * Ez ma megmarad (Viktor, 2026-09-13): az összevonás egy közös szolgáltatóba
 * az `App.tsx` szerkezetéhez nyúlna, a #25 utolsó szelete közben. Aki
 * hozzányúl, ITT kezdje.
 *
 * ═══ A TENSOFT HELYE ═══
 *
 * A NAV alatt, a lapon, irodánként egy kapcsolat (artboard, 2026-09-13). A
 * kártya a feltöltő-tokeneket kezeli (#54) — `TensoftConnection`.
 *
 * ⚠️ EZÉRT KAP MÁR `onLayer`-t ez a szelet: a token-kártyának VAN rétege (a
 * visszavonás megerősítése, és a kiadott token doboza, ami az `Escape`-et
 * nyeli). A NAV-panelnek továbbra sincs.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavCompanyPicker,
  NavCredentialFields,
  NavCredentialNotices,
  NavCredentialSaved,
} from '@/features/nav/NavCredentialParts';
import { useNavCredentialEditor } from '@/features/nav/useNavCredentialEditor';
import { TensoftConnection } from '@/features/tensoft/TensoftConnection';
import type { LayerRegistrar } from './useSettingsLayer';

export function NavSettings({ onLayer }: { onLayer: LayerRegistrar }) {
  const { t } = useTranslation();
  const editor = useNavCredentialEditor();
  const listId = useId();

  /**
   * A KÉT MEGÁLLÍTÓ ÁG — ugyanaz a gondolat, mint a `companyLoadState`-ben: ha
   * a lista meg sem érkezett, a kereső egy üres dobozt mutatna, ami
   * elromlottnak látszik. A hibamondatot a `NavCredentialNotices` adja; itt
   * csak azt döntjük el, kerül-e MÖGÉ szerkesztő.
   *
   * ⚠️ A `loading` CSAK AZ ELSŐ betöltésnél állít meg. A `reload()` a mentés
   * után is `loading`-ra vált (`useNavCredentials`), és ott a félkész lista
   * eltüntetése villogást okozna — a `firstLoad` ezért nézi a lista ürességét
   * is.
   */
  const firstLoad = editor.status === 'loading' && editor.totalCount === 0;
  const blocked = editor.status === 'forbidden' || editor.status === 'listFailed' || firstLoad;

  return (
    <div className="navSettings">
      <NavCredentialNotices status={editor.status} detail={editor.detail} />

      {firstLoad ? <p className="subtitle">{t('navSettings.loading')}</p> : null}

      {blocked ? null : (
        <div className="navSettings__split">
          <div className="card navSettings__pick">
            <NavCompanyPicker editor={editor} listId={listId} />
            <p className="navSettings__count">
              {t('navSettings.counts', {
                configured: editor.configuredCount,
                total: editor.totalCount,
              })}
            </p>
          </div>

          <div className="card navSettings__edit">
            <NavCredentialSaved editor={editor} />
            {editor.selected === null ? (
              /* ⚠️ KIMONDVA, NEM ÜRESEN — ugyanaz a szabály, mint a lenti
                 Tensoft-blokknál. Egy üres jobb hasáb a könyvelőt arra viszi,
                 hogy a saját gépét gyanúsítsa. */
              <p className="empty">{t('navSettings.pickCompany')}</p>
            ) : (
              <>
                <h3 className="navSettings__who">
                  {editor.selected.companyName}{' '}
                  {/* ⚠️ A SZÓKÖZ A JELÖLÉSBEN VAN, NEM CSAK MARGÓBAN. A margó a
                      SZEMNEK választja el a kettőt, a hozzáférhető névbe
                      viszont nem kerül bele: a képernyőolvasó „…Kivitelező
                      Kft.12345678-2-06"-ot mondott, egy szóként. Mérve a
                      dev-en, 2026-09-14. */}
                  <span className="navSettings__tax">{editor.selected.taxNumber}</span>
                </h3>
                <NavCredentialFields editor={editor} listId={listId} />
              </>
            )}
          </div>
        </div>
      )}

      <TensoftConnection onLayer={onLayer} />
    </div>
  );
}
